import React, { useEffect, useState } from "react";
import styles from "./academic-view.module.css";
import { Button, Col, Empty, Pagination, Row, Typography } from "antd";
import { AppstoreOutlined, UnorderedListOutlined } from "@ant-design/icons";
import CustomCollapseTable from "../../../../common/custom-collapse-table/custom-collapse-table";
import AcademicNonacademicStatCard from "../../../../components/academic-nonacademic-stat-card/academic-nonacademic-stat-card";
import CustomTag from "../../../../common/custom-tag/custom-tag";
import { cardColorArr } from "../../../../constants/common";
import { useNavigate } from "react-router-dom";
import { fetchSchoolLevelStatistics } from "../../../../services/student";
import RoutesConstant from "../../../../constants/routes";

const AcademicView = ({ triggerRefetch, selectedYear, selectedYearName }) => {
  const navigate = useNavigate();
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [schoolLevelData, setSchoolLevelData] = useState(false);
  const [viewStudents, setViewStudents] = useState(true);

  const columns = [
    { title: "Level", dataIndex: "level_name", key: "level_name" },
    {
      title: "Sections",
      dataIndex: "class_name",
      key: "class_name",
      render: (sections) => {
        if (sections?.length) {
          const getDeterministicColor = (key) => {
            let hash = 0;
            for (let i = 0; i < key.length; i++) {
              hash = key.charCodeAt(i) + ((hash << 5) - hash);
            }
            const index = Math.abs(hash) % cardColorArr.length;
            return cardColorArr[index];
          };

          const renderTag = (section) => {
            const colorStyle = getDeterministicColor(section);
            return (
              <CustomTag
                key={section}
                text={section}
                color={colorStyle.color}
                background={colorStyle.background}
                borderColor={colorStyle.border}
                cursor={"pointer"}
              />
            );
          };

          const sectionTags = Array.isArray(sections)
            ? sections.map(renderTag)
            : [renderTag(sections)];

          return (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {sectionTags}
            </div>
          );
        } else {
          return "-";
        }
      },
    },
    {
      title: "Total Students",
      dataIndex: "total_students",
      key: "total_students",
    },
    { title: "Male Students", dataIndex: "total_male", key: "total_male" },
    {
      title: "Female Students",
      dataIndex: "total_female",
      key: "total_female",
    },
    { title: "Teachers", dataIndex: "total_teachers", key: "total_teachers" },
  ];

  const menuItems = (record) => [
    {
      key: "1",
      label: "View Details",
      onClick: () => handleViewDetails(record),
    },
  ];

  useEffect(() => {
    if (selectedYear) {
      const timeOut = setTimeout(() => {
        setPageNo(1);
        getSchoolLevelStatistics();
      }, 500);
      return () => {
        clearTimeout(timeOut);
      };
    }
  }, [triggerRefetch, selectedYear]);

  const getSchoolLevelStatistics = async (
    tempPage = pageNo,
    tempPageSize = pageSize
  ) => {
    try {
      setLoading(true);
      const payload = {
        page: tempPage,
        pageSize: tempPageSize,
        academic_year_id: selectedYear,
      };
      const response = await fetchSchoolLevelStatistics(payload);
      if (response?.data) {
        const tempList = response?.data?.data?.levels?.map((e, i) => {
          return {
            key: i + 1,
            level_id: e?.level_id,
            level_name: e?.level_name,
            children: e?.classes,
            class_name: e?.classes?.map((classroom) => classroom.class_name),
            total_students: e?.classes.reduce(
              (acc, classroom) => acc + classroom.total_students,
              0
            ),
            total_male: e?.classes.reduce(
              (acc, classroom) => acc + classroom.total_male,
              0
            ),
            total_female: e?.classes.reduce(
              (acc, classroom) => acc + classroom.total_female,
              0
            ),
            total_teachers: e?.classes.reduce(
              (acc, classroom) => acc + classroom.total_teachers,
              0
            ),
          };
        });
        setSchoolLevelData(tempList);
        setTotalCount(response?.data?.data?.totalRecords);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    }
  };

  const handleChangePage = (page, pageSize) => {
    setPageNo(page);
    setPageSize(pageSize);
    getSchoolLevelStatistics(page, pageSize);
  };

  const handleViewDetails = (record) => {
    const levelData = schoolLevelData.find((lvl) =>
      lvl.children.some((e) => e.classroom_id === record.classroom_id)
    );
    const levelName = levelData?.level_name ?? "";
    const sectionName = record?.class_name ?? "";
    const sectionID = record?.classroom_id ?? "";

    navigate({
      pathname: RoutesConstant.STUDENT_LIST,
      search: new URLSearchParams({
        id: sectionID,
        level: levelName,
        class: sectionName,
        "year-id": selectedYear,
        "year-name": selectedYearName,
      }).toString(),
    });
  };

  return (
    <div className={styles.main_container}>
      <div className={styles.title_container}>
        <Typography className={styles.title}>Student By Level</Typography>
        <div className={styles.view_style_options}>
          <Button
            type={viewStudents ? "primary" : "default"}
            icon={<UnorderedListOutlined />}
            onClick={() => setViewStudents(true)}
          >
            List View
          </Button>

          <Button
            type={viewStudents ? "default" : "primary"}
            icon={<AppstoreOutlined />}
            onClick={() => setViewStudents(false)}
          >
            Card View
          </Button>
        </div>
      </div>

      {viewStudents ? (
        <div style={{ overflowX: "auto" }}>
          <CustomCollapseTable
            columns={columns}
            dataSource={schoolLevelData}
            collapsible
            loading={loading}
            showMoreIcon={true}
            total={totalCount}
            menuItems={menuItems}
            onChildClick={handleViewDetails}
            pageSize={pageSize}
            currentPage={pageNo}
            handlePageChange={handleChangePage}
          />
        </div>
      ) : (
        <div className={styles.levels_container}>
          {schoolLevelData.map((item, index) => (
            <div className={styles.level_data} key={index}>
              <h3>LEVEL&nbsp;{item?.level_name}</h3>
              <Row gutter={[16, 16]}>
                {item?.children?.length ? (
                  item?.children?.map((child, child_index) => (
                    <Col
                      style={{ cursor: "pointer" }}
                      onClick={() => handleViewDetails(child)}
                    >
                      <AcademicNonacademicStatCard
                        key={child_index}
                        level={child.class_name}
                        total_students={child.total_students}
                        total_male={child.total_male}
                        total_female={child.total_female}
                        total_teachers={child.total_teachers}
                        is_academic={true}
                        chart_label_1={"Male"}
                        chart_label_2={"Female"}
                        chart_color_1={"rgba(23, 160, 34, 1)"}
                        chart_color_2={"rgba(172, 216, 30, 1)"}
                      />
                    </Col>
                  ))
                ) : (
                  <Col>
                    <div className={styles.empty_card}>
                      <Empty description="No classes available" />
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          ))}
          <Pagination
            current={pageNo}
            pageSize={pageSize}
            total={totalCount}
            onChange={handleChangePage}
          />
        </div>
      )}
    </div>
  );
};

export default AcademicView;
